import React, { useState } from "react";
import { Search, Mail, Phone, Users, UserCircle } from "lucide-react";
import { Input } from "../ui/input";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "../ui/avatar";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";

const staffData = [
  { id: 1, name: "[Staff Name]", title: "Principal", department: "Administration", email: "principal@ycpschool.edu", phone: "ext. 101", image: "" },
  { id: 2, name: "[Staff Name]", title: "Vice Principal", department: "Administration", email: "viceprincipal@ycpschool.edu", phone: "ext. 102", image: "" },
  { id: 3, name: "[Staff Name]", title: "Physics Teacher", department: "Science", email: "physics@ycpschool.edu", phone: "ext. 201", image: "" },
  { id: 4, name: "[Staff Name]", title: "History Teacher", department: "Social Studies", email: "history@ycpschool.edu", phone: "ext. 202", image: "" },
  { id: 5, name: "[Staff Name]", title: "Spanish Teacher", department: "Languages", email: "spanish@ycpschool.edu", phone: "ext. 203", image: "" },
  { id: 6, name: "[Staff Name]", title: "Computer Science", department: "Technology", email: "ict@ycpschool.edu", phone: "ext. 204", image: "" },
  { id: 7, name: "[Staff Name]", title: "Art Teacher", department: "Arts", email: "arts@ycpschool.edu", phone: "ext. 205", image: "" },
  { id: 8, name: "[Staff Name]", title: "PE Director", department: "Athletics", email: "pe@ycpschool.edu", phone: "ext. 206", image: "" },
  { id: 9, name: "[Staff Name]", title: "Librarian", department: "Library", email: "library@ycpschool.edu", phone: "ext. 207", image: "" },
  { id: 10, name: "[Staff Name]", title: "Math Teacher", department: "Mathematics", email: "math@ycpschool.edu", phone: "ext. 208", image: "" },
  { id: 11, name: "[Staff Name]", title: "English Teacher", department: "English", email: "english@ycpschool.edu", phone: "ext. 209", image: "" },
  { id: 12, name: "[Staff Name]", title: "Biology Teacher", department: "Science", email: "biology@ycpschool.edu", phone: "ext. 210", image: "" },
  { id: 13, name: "[Staff Name]", title: "Counselor", department: "Counseling", email: "counselor@ycpschool.edu", phone: "ext. 211", image: "" },
  { id: 14, name: "[Staff Name]", title: "Music Director", department: "Arts", email: "music@ycpschool.edu", phone: "ext. 212", image: "" },
  { id: 15, name: "[Staff Name]", title: "Chemistry Teacher", department: "Science", email: "chemistry@ycpschool.edu", phone: "ext. 213", image: "" },
];

export const StaffDirectory = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [department, setDepartment] = useState("all");
  const [selectedStaff, setSelectedStaff] = useState(null);

  const departments = ["all", ...new Set(staffData.map(s => s.department))];

  const filteredStaff = staffData.filter(s => {
    const matchesDept = department === "all" || s.department === department;
    const matchesSearch = s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         s.title.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesDept && matchesSearch;
  });

  return (
    <section className="py-16 bg-gradient-to-b from-orange-400 to-yellow-400">
      <div className="max-w-7xl mx-auto px-4">
        <h2 className="text-3xl font-bold text-[#002147] mb-8 text-center">Staff Directory</h2>
        
        <div className="flex flex-wrap gap-4 justify-center mb-8">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <Input
              placeholder="Search staff..."
              className="pl-10 w-64"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="staff-search"
            />
          </div>
          <Select value={department} onValueChange={setDepartment}>
            <SelectTrigger className="w-48" data-testid="staff-department-filter">
              <SelectValue placeholder="Department" />
            </SelectTrigger>
            <SelectContent>
              {departments.map(dept => (
                <SelectItem key={dept} value={dept}>
                  {dept === "all" ? "All Departments" : dept}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
          {filteredStaff.map((staff) => (
            <Card 
              key={staff.id} 
              className="card-hover cursor-pointer text-center"
              onClick={() => setSelectedStaff(staff)}
              data-testid={`staff-card-${staff.id}`}
            >
              <CardContent className="p-6">
                <Avatar className="w-20 h-20 mx-auto mb-4">
                  {staff.image ? <AvatarImage src={staff.image} alt={staff.name} /> : null}
                  <AvatarFallback className="bg-[#002147]/10 text-[#002147]">
                    <UserCircle size={36} />
                  </AvatarFallback>
                </Avatar>
                <h3 className="font-semibold text-[#002147]">{staff.name}</h3>
                <p className="text-sm text-gray-600">{staff.title}</p>
                <Badge variant="secondary" className="mt-2">{staff.department}</Badge>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Staff Detail Modal */}
        <Dialog open={!!selectedStaff} onOpenChange={() => setSelectedStaff(null)}>
          <DialogContent className="sm:max-w-md">
            {selectedStaff && (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-4">
                    <Avatar className="w-16 h-16">
                      {selectedStaff.image ? <AvatarImage src={selectedStaff.image} alt={selectedStaff.name} /> : null}
                      <AvatarFallback className="bg-[#002147]/10 text-[#002147]">
                        <UserCircle size={28} />
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <DialogTitle>{selectedStaff.name}</DialogTitle>
                      <DialogDescription>{selectedStaff.title}</DialogDescription>
                    </div>
                  </div>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="flex items-center gap-3">
                    <Mail size={18} className="text-gray-400" />
                    <a href={`mailto:${selectedStaff.email}`} className="text-[#002147] hover:underline">
                      {selectedStaff.email}
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone size={18} className="text-gray-400" />
                    <span>{selectedStaff.phone}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Users size={18} className="text-gray-400" />
                    <span>{selectedStaff.department}</span>
                  </div>
                </div>
                <DialogFooter className="mt-6">
                  <Button className="bg-[#002147] w-full">
                    <Mail size={16} className="mr-2" /> Send Message
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};

export default StaffDirectory;
